import React, { useState, useEffect } from 'react';
import './Calculator.css';

import { Heading } from '../../components';

import { FaBuilding, FaClock, FaLayerGroup, FaLeaf, FaRulerHorizontal, FaSeedling, FaTree } from 'react-icons/fa';

const CostCalculator = () => {

  // State hooks for form inputs
  const [serviceType, setServiceType] = useState(''); // Selected service type (e.g., lawn maintenance, garden design)
  const [propertyType, setPropertyType] = useState(''); // Selected property type (residential or commercial)
  const [areaSize, setAreaSize] = useState(''); // Area size in square meters
  const [gardenStyle, setGardenStyle] = useState(''); // Selected garden style (e.g., native, formal)
  const [numberOfPlants, setNumberOfPlants] = useState(''); // Number of plants
  const [projectUrgency, setProjectUrgency] = useState(''); // Project urgency (e.g., ASAP, no rush)
  const [soilType, setSoilType] = useState(''); // Selected soil type (e.g., loam, sandy)
  const [extraServices, setExtraServices] = useState({
  
    // State for additional services (checkboxes)
    automatedIrrigation: false,
    customPathways: false,
    outdoorSeating: false,
  });
  const [totalCost, setTotalCost] = useState(0); // Total calculated cost

  // useEffect to recalculate the total cost whenever any input changes
  useEffect(() => {
    updateTotal();
  }, [serviceType, propertyType, areaSize, gardenStyle, numberOfPlants, projectUrgency, soilType, extraServices]);

  // Function to calculate the total cost based on form inputs
  const updateTotal = () => {

    let total = 0;

    // Calculate base cost based on selected service type and property type
    if (serviceType && propertyType) {
      total += getServiceCost(serviceType, propertyType);
    }

    // Add cost for area size ($10 per square meter)
    total += areaSize * 10;

    // Add cost for number of plants ($5 per plant)
    total += numberOfPlants * 5;

    // Add cost for garden style based on selected style
    total += getGardenStyleCost(gardenStyle);

    // Add cost based on project urgency
    total += getProjectUrgencyCost(projectUrgency);

    // Add cost based on selected soil type
    total += getSoilTypeCost(soilType);

    // Add additional costs based on selected extra services (checkboxes)
    if (extraServices.automatedIrrigation) total += 500;
    if (extraServices.customPathways) total += 300;
    if (extraServices.outdoorSeating) total += 700;

    // Update the total cost state
    setTotalCost(total);

  };

  // Function to calculate base cost based on service type and property type
  const getServiceCost = (serviceType, propertyType) => {
    
    const baseCosts = {
      'lawn-maintenance': 100,
      'garden-design': 200,
      'tree-planting': 150,
      'hardscaping': 300,
      'water-features': 400,
      'outdoor-lighting': 250,
    };

    // Apply a multiplier for commercial properties
    const propertyMultiplier = propertyType === 'commercial' ? 1.5 : 1;
    return baseCosts[serviceType] * propertyMultiplier || 0;

  };

  // Function to retrieve cost based on selected garden style
  const getGardenStyleCost = (gardenStyle) => {
    const gardenStyleCosts = {
      'native': 100,
      'formal': 200,
      'cottage': 150,
      'japanese': 250,
      'modern': 300,
      'eclectic': 200,
      'default': 0,
    };
    return gardenStyleCosts[gardenStyle] || 0;
  };

  // Function to retrieve cost based on project urgency
  const getProjectUrgencyCost = (projectUrgency) => {
    const projectUrgencyCosts = {
      'asap': 300,
      'within-a-month': 200,
      'within-three-months': 100,
      'no-rush': 0,
    };
    return projectUrgencyCosts[projectUrgency] || 0;
  };

  // Function to retrieve cost based on selected soil type
  const getSoilTypeCost = (soilType) => {
    const soilTypeCosts = {
      'loam': 50,
      'sandy': 30,
      'clay': 40,
      'silt': 45,
      'peat': 60,
      'chalk': 55,
      'rocky': 70,
    };
    return soilTypeCosts[soilType] || 0;
  };

  // Handle checkbox changes for extra services
  const handleCheckboxChange = (e) => {
    setExtraServices({
      ...extraServices,
      [e.target.name]: e.target.checked,
    });
  };

  return (
    <section className="cost-calculator linear-bg">
      <div className="box-container">
        <div className="content">
          <form className="calculator-form dark-form">

            {/* Heading component with title and subtitle */}
            <Heading title={'Cost Calculator'} subTitle={'Estimate Your Garden Expenses'} sideHeading linear />

            {/* Service Type Input */}
            <div className="input-box">
              <div className="select">
                <div className="select-container">
                  <label htmlFor="service-type"><FaLeaf /></label>
                  <select className="select-box" id="service-type" name="service-type" value={serviceType} onChange={(e) => setServiceType(e.target.value)} required>
                    <option value="">Service Type</option>
                    <option value="lawn-maintenance">Lawn Maintenance</option>
                    <option value="garden-design">Garden Design</option>
                    <option value="tree-planting">Tree Planting</option>
                    <option value="hardscaping">Hardscaping</option>
                    <option value="water-features">Water Features</option>
                    <option value="outdoor-lighting">Outdoor Lighting</option>
                  </select>
                  <div className="icon-container">
                    <i className="fas fa-angle-down"></i>
                  </div>
                </div>
              </div>
            </div>

            {/* Property Type and Area Size Inputs */}
            <div className="input-box">
              <div className="select">
                <div className="select-container">
                  <label htmlFor="property-type"><FaBuilding /></label>
                  <select className="select-box" id="property-type" name="property-type" value={propertyType} onChange={(e) => setPropertyType(e.target.value)} required>
                    <option value="">Property Type</option>
                    <option value="residential">Residential</option>
                    <option value="commercial">Commercial</option>
                  </select>
                  <div className="icon-container">
                    <i className="fas fa-angle-down"></i>
                  </div>
                </div>
              </div>

              <div className="input-field">
                <label htmlFor="area-size"><FaRulerHorizontal /></label>
                <input type="number" name="area-size" className="box" id="area-size" min="0" placeholder="Area Size (sq m)" value={areaSize} onChange={(e) => setAreaSize(e.target.value)} required />
              </div>
            </div>

            {/* Garden Style and Number of Plants Inputs */}
            <div className="input-box">
              <div className="select">
                <div className="select-container">
                  <label htmlFor="garden-style"><FaTree /></label>
                  <select className="select-box" id="garden-style" name="garden-style" value={gardenStyle} onChange={(e) => setGardenStyle(e.target.value)} required>
                    <option value="">Garden Style</option>
                    <option value="native">Native</option>
                    <option value="formal">Formal</option>
                    <option value="cottage">Cottage</option>
                    <option value="japanese">Japanese</option>
                    <option value="modern">Modern</option>
                    <option value="eclectic">Eclectic</option>
                    <option value="default">No Preference</option>
                  </select>
                  <div className="icon-container">
                    <i className="fas fa-angle-down"></i>
                  </div>
                </div>
              </div>

              <div className="input-field">
                <label htmlFor="number-of-plants"><FaSeedling /></label>
                <input type="number" name="number-of-plants" className="box" id="number-of-plants" min="0" placeholder="Number of plants" value={numberOfPlants} onChange={(e) => setNumberOfPlants(e.target.value)} required />
              </div>

            </div>

            {/* Project Urgency and Soil Type Inputs */}
            <div className="input-box">

              <div className="select">
                <div className="select-container">
                  <label htmlFor="project-urgency"><FaClock /></label>
                  <select className="select-box" id="project-urgency" name="project-urgency" value={projectUrgency} onChange={(e) => setProjectUrgency(e.target.value)} required>
                    <option value="">Project Urgency</option>
                    <option value="asap">ASAP</option>
                    <option value="within-a-month">Within a month</option>
                    <option value="within-three-months">Within 3 months</option>
                    <option value="no-rush">No rush</option>
                  </select>
                  <div className="icon-container">
                    <i className="fas fa-angle-down"></i>
                  </div>
                </div>
              </div>

              <div className="select">
                <div className="select-container">
                  <label htmlFor="soil-type"><FaLayerGroup /></label>
                  <select className="select-box" id="soil-type" name="soil-type" value={soilType} onChange={(e) => setSoilType(e.target.value)} required>
                    <option value="">Soil Type</option>
                    <option value="loam">Loam</option>
                    <option value="sandy">Sandy</option>
                    <option value="clay">Clay</option>
                    <option value="silt">Silt</option>
                    <option value="peat">Peat</option>
                    <option value="chalk">Chalk</option>
                    <option value="rocky">Rocky</option>
                  </select>
                  <div className="icon-container">
                    <i className="fas fa-angle-down"></i>
                  </div>
                </div>
              </div>
            </div>

            {/* Extra Services Checkboxes */}
            <div className="extra-services">
              <div className="checkbox-label">
                <input type="checkbox" id="automated-irrigation-checkbox" name="automatedIrrigation" checked={extraServices.automatedIrrigation} onChange={handleCheckboxChange} />
                <label htmlFor="automated-irrigation-checkbox">Automated Irrigation</label>
                <div className="checkbox-indicator"></div>
              </div>

              <div className="checkbox-label">
                <input type="checkbox" id="custom-pathways-checkbox" name="customPathways" checked={extraServices.customPathways} onChange={handleCheckboxChange} />
                <label htmlFor="custom-pathways-checkbox">Custom Pathways</label>
                <div className="checkbox-indicator"></div>
              </div>

              <div className="checkbox-label">
                <input type="checkbox" id="outdoor-seating-checkbox" name="outdoorSeating" checked={extraServices.outdoorSeating} onChange={handleCheckboxChange} />
                <label htmlFor="outdoor-seating-checkbox">Outdoor Seating</label>
                <div className="checkbox-indicator"></div>
              </div>
            </div>

            {/* Display Total Cost */}
            <div className="cost-total">
              <h5>Total Cost:</h5>
              <span>${totalCost}</span>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default CostCalculator;